import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneTilt, X } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useCollaborationStore } from "../../stores/collaboration";

interface CommentPopoverProps {
  onSubmit: (content: string, lineStart: number, lineEnd: number) => void;
}

const spring = { type: "spring" as const, stiffness: 500, damping: 30, mass: 0.5 };

export function CommentPopover({ onSubmit }: CommentPopoverProps) {
  const popover = useCollaborationStore((s) => s.commentPopover);
  const close = useCollaborationStore((s) => s.closeCommentPopover);
  const [content, setContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (popover) {
      setContent("");
      setTimeout(() => textareaRef.current?.focus(), 60);
    }
  }, [popover]);

  const handleSubmit = () => {
    if (!content.trim() || !popover) return;
    onSubmit(content.trim(), popover.lineStart, popover.lineEnd);
    setContent("");
    close();
  };

  return (
    <AnimatePresence>
      {popover && (
        <motion.div
          key="comment-popover"
          initial={{ opacity: 0, y: 6, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.95 }}
          transition={spring}
          className={cn(
            "absolute z-50 w-60",
            "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
            "rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)]",
            "backdrop-blur-[var(--blur-md)]",
            "p-2.5",
          )}
          style={{
            left: Math.max(130, popover.position.x),
            top: popover.position.y + 12,
            transform: "translate(-50%, 0)",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              L{popover.lineStart}
              {popover.lineStart !== popover.lineEnd ? `–${popover.lineEnd}` : ""}
            </span>
            <button
              onClick={close}
              onMouseDown={(e) => e.preventDefault()}
              className={cn(
                "p-0.5 rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
              )}
            >
              <X size={12} />
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
            className={cn(
              "w-full resize-none rounded-[var(--radius-sm)]",
              "border border-[var(--border-hairline)]",
              "bg-transparent px-2 py-1.5 text-[12px] text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)] outline-none",
              "focus:border-[var(--color-cool-azure)]/40",
              "transition-colors duration-100",
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
              if (e.key === "Escape") close();
            }}
          />

          <div className="flex items-center justify-between mt-2">
            <span className="text-[9px] text-[var(--text-muted)]">
              {navigator.platform?.includes("Mac") ? "⌘" : "Ctrl"}+↵
            </span>
            <button
              onClick={handleSubmit}
              disabled={!content.trim()}
              onMouseDown={(e) => e.preventDefault()}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2.5 py-1",
                "text-[11px] font-medium transition-all duration-100",
                content.trim()
                  ? "bg-[var(--color-cool-azure)] text-white hover:brightness-110"
                  : "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                "disabled:opacity-50 disabled:pointer-events-none",
              )}
            >
              <PaperPlaneTilt size={11} weight="fill" />
              Comment
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
